import type { EnemyDef } from '@gra/content';
import { bossIntent } from './boss';
import { shieldDamage } from './effects';
import { nextRandom } from './rng';
import type { Enemy, GameState } from './types';
import { insideObstacle } from './physics';

/**
 * Zachowania wrogów (z `enemy.behavior` w danych):
 *  chase    — idzie prosto na bohatera,
 *  ranged   — trzyma dystans i strzela po krótkim zamachu,
 *  charger  — po zamachu szarżuje w linii prostej,
 *  exploder — podbiega, zapala się i wybucha,
 *  boss     — idzie powoli, na zmianę: pierścień pocisków dookoła i wachlarz w bohatera;
 *             boss z `def.boss` ma własne ataki z zapowiedzią na ziemi i szał (boss.ts).
 * Zwraca prędkość, z jaką wróg chce się ruszyć w tym kroku (przed rozpychaniem i omijaniem przeszkód).
 */
export function enemyIntent(s: GameState, e: Enemy, def: EnemyDef, dx: number, dy: number, dist: number, dt: number): { vx: number; vy: number } {
  if (def.boss) return bossIntent(s, e, def, dx, dy, dist, dt);
  const chase = { vx: (dx / dist) * e.speed, vy: (dy / dist) * e.speed };
  const atk = def.attack;
  if (def.behavior === 'chase' || !atk) return chase;

  // Pierwszy atak po losowym czasie (żeby grupa nie strzelała równo); czaszka zapala się od razu.
  e.timer = (e.timer ?? (def.behavior === 'exploder' ? 0 : atk.interval * (0.4 + nextRandom(s) * 0.6))) - dt;
  const phase = e.phase ?? 'move';
  e.phaseTime = (e.phaseTime ?? 0) + dt;

  if (phase === 'windup') {
    if (e.phaseTime < atk.windup) return { vx: 0, vy: 0 };
    release(s, e, def, dx, dy, dist);
    return { vx: 0, vy: 0 };
  }
  if (phase === 'charge') {
    // Szarża: stały kierunek, duża prędkość, kończy się po czasie albo na bohaterze.
    if (e.phaseTime > atk.range / atk.speed + 0.15) setPhase(e, 'move');
    return { vx: (e.dirX ?? 0) * atk.speed, vy: (e.dirY ?? 0) * atk.speed };
  }

  // Faza ruchu: kiedy atak gotowy i bohater w zasięgu — zamach (zapowiedź ataku).
  if (e.timer <= 0 && dist <= atk.range) {
    setPhase(e, 'windup');
    e.dirX = dx / dist;
    e.dirY = dy / dist;
    s.events.push({ type: 'enemyWindup', id: e.id, x: e.x, y: e.y, angle: Math.atan2(dy, dx), duration: atk.windup });
    return { vx: 0, vy: 0 };
  }
  if (def.behavior === 'ranged') {
    // Strzelec trzyma się na dystans: podchodzi, gdy za daleko, cofa się, gdy bohater za blisko.
    if (dist > atk.range * 0.85) return chase;
    if (dist < atk.range * 0.55) return { vx: -chase.vx * 0.7, vy: -chase.vy * 0.7 };
    return { vx: 0, vy: 0 };
  }
  if (def.behavior === 'boss') return { vx: chase.vx * 0.8, vy: chase.vy * 0.8 };
  return chase;
}

function setPhase(e: Enemy, phase: 'move' | 'windup' | 'charge'): void {
  e.phase = phase;
  e.phaseTime = 0;
}

/** Koniec zamachu: strzał, szarża, wybuch albo salwa bossa. */
function release(s: GameState, e: Enemy, def: EnemyDef, dx: number, dy: number, dist: number): void {
  const atk = def.attack!;
  e.timer = atk.interval;
  setPhase(e, 'move');
  const aim = Math.atan2(dy, dx);
  switch (def.behavior) {
    case 'ranged':
      for (let i = 0; i < atk.count; i++) shoot(s, e, def, aim + (i - (atk.count - 1) / 2) * 0.18);
      break;
    case 'charger':
      setPhase(e, 'charge');
      e.dirX = dx / dist;
      e.dirY = dy / dist;
      s.events.push({ type: 'enemyCharge', id: e.id });
      break;
    case 'exploder':
      explode(s, e, atk.radius, atk.damage);
      break;
    case 'boss': {
      e.cycle = (e.cycle ?? 0) + 1;
      if (e.cycle % 2 === 1) {
        // Pierścień pocisków dookoła — trzeba znaleźć lukę.
        const offset = nextRandom(s) * Math.PI * 2;
        for (let i = 0; i < atk.count; i++) shoot(s, e, def, offset + (i / atk.count) * Math.PI * 2);
      } else {
        // Wachlarz w bohatera.
        for (let i = 0; i < 5; i++) shoot(s, e, def, aim + (i - 2) * 0.16);
      }
      s.events.push({ type: 'bossCast', id: e.id, x: e.x, y: e.y, ring: e.cycle % 2 === 1 });
      break;
    }
  }
}

export function shoot(s: GameState, e: Enemy, def: EnemyDef, angle: number): void {
  const atk = def.attack!;
  s.hostileShots.push({
    id: s.nextId++,
    defId: def.id,
    x: e.x + Math.cos(angle) * e.radius,
    y: e.y + Math.sin(angle) * e.radius,
    vx: Math.cos(angle) * atk.speed,
    vy: Math.sin(angle) * atk.speed,
    damage: atk.damage,
    radius: atk.shotRadius,
    life: (atk.range * 1.6) / atk.speed,
  });
}

/** Wybuch: rani bohatera w promieniu, sam wróg znika (bez nagrody — bohater go nie pokonał). */
function explode(s: GameState, e: Enemy, radius: number, damage: number): void {
  const p = s.player;
  if (Math.hypot(p.x - e.x, p.y - e.y) <= radius + s.content.game.playerRadius) hurtPlayer(s, damage);
  e.hp = 0;
  s.events.push({ type: 'explosion', x: e.x, y: e.y, radius });
  s.events.push({ type: 'enemyDied', id: e.id, defId: e.defId, x: e.x, y: e.y });
}

/** Obrażenia od ataku wroga (pancerz zmniejsza je tak samo jak obrażenia kontaktowe). */
export function hurtPlayer(s: GameState, amount: number): void {
  const p = s.player;
  const dealt = shieldDamage(s, amount * (100 / (100 + p.stats.armor)), true);
  if (dealt <= 0) return;
  p.hp -= dealt;
  s.events.push({ type: 'playerHurt', amount: dealt });
}

/** Szarżujący wróg, który dobiegł do bohatera: jednorazowy cios i koniec szarży. */
export function chargeImpact(s: GameState, e: Enemy, def: EnemyDef): void {
  if (e.phase !== 'charge' || !def.attack) return;
  hurtPlayer(s, def.attack.damage);
  setPhase(e, 'move');
}

/** Pociski wrogów: lecą prosto, trafiają bohatera. */
export function updateHostileShots(s: GameState, dt: number): void {
  const p = s.player;
  const pr = s.content.game.playerRadius;
  for (const sh of s.hostileShots) {
    sh.x += sh.vx * dt;
    sh.y += sh.vy * dt;
    sh.life -= dt;
    // Pocisk wroga rozbija się o drzewo albo grób — za przeszkodą można się schować.
    if (sh.life > 0 && s.content.game.physics.hostileShotsHitObstacles && insideObstacle(s, sh.x, sh.y)) {
      s.events.push({ type: 'shotBlocked', x: sh.x, y: sh.y, defId: sh.defId });
      sh.life = 0;
      continue;
    }
    if (sh.life > 0 && Math.hypot(p.x - sh.x, p.y - sh.y) < pr + sh.radius) {
      hurtPlayer(s, sh.damage);
      s.events.push({ type: 'shotHit', x: sh.x, y: sh.y, defId: sh.defId });
      sh.life = 0;
    }
  }
  s.hostileShots = s.hostileShots.filter((sh) => sh.life > 0);
}
